import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const suppliers = [
  {
    name: 'Paneles Leonera',
    country: 'Chile',
    flag: '🇨🇱',
    specialty: 'ACx, BCx, CDx, T1-11, Siding Plywood',
    description: 'Leading Chilean manufacturer of premium structural and siding plywood panels for residential and commercial construction markets.',
  },
  {
    name: 'Forestal y Aserraderos Leonera',
    country: 'Chile',
    flag: '🇨🇱',
    specialty: 'Construction Lumber, KD Lumber, Pallet Lumber',
    description: 'Vertically integrated forestry and sawmill operation producing high-quality structural, dimensional, and kiln-dried lumber.',
  },
  {
    name: 'Forestal Leonera Comercializadora Ltda.',
    country: 'Chile',
    flag: '🇨🇱',
    specialty: 'Molding, KD Lumber, Pallet Lumber, Treated Lumber & Plywood',
    description: 'Chilean trading arm of the Leonera group specializing in molding profiles, treated products, and kiln-dried lumber for export.',
  },
  {
    name: 'Dexco',
    country: 'Brazil',
    flag: '🇧🇷',
    specialty: 'HDF, MDF, MDP',
    description: 'One of Brazil\'s largest and most innovative engineered wood panel manufacturers with a strong sustainability commitment.',
  },
  {
    name: 'Duobrax',
    country: 'Brazil',
    flag: '🇧🇷',
    specialty: 'Pallet Lumber, KD Lumber',
    description: 'Brazilian specialist in kiln-dried pallet lumber and packaging wood products for industrial and export applications.',
  },
  {
    name: 'Mercopallet',
    country: 'Argentina',
    flag: '🇦🇷',
    specialty: 'Fencing, Pallet Lumber',
    description: 'Argentine leader in sustainable fencing products and kiln-dried pallet lumber for domestic and export markets.',
  },
  {
    name: 'Louisiana Pacific',
    country: 'Brazil & Chile',
    flag: '🌎',
    specialty: 'OSB',
    description: 'Global leader in high-performance OSB building products, operating modern production facilities across South America.',
  },
  {
    name: 'Comercializadora T&P',
    country: 'Chile',
    flag: '🇨🇱',
    specialty: 'Molding, KD Lumber, Pallet Lumber, Construction Lumber',
    description: 'Chilean trading company specializing in molding profiles, kiln-dried lumber, and construction lumber for the export market.',
  },
  {
    name: 'TULSA',
    country: 'Chile',
    flag: '🇨🇱',
    specialty: 'HDO, MDO, PSF Plywood',
    description: 'Chilean manufacturer of premium specialty plywood products including HDO, MDO, and PSF grades for demanding applications.',
  },
];

export default function SuppliersSection() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="suppliers" className="py-24 lg:py-32 bg-[#F7F3EC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          ref={ref}
          className={`flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2D5E3F]/10 rounded-full mb-6">
              <span className="w-2 h-2 rounded-full bg-[#2D5E3F] inline-block"></span>
              <span className="text-[#2D5E3F] text-xs font-semibold tracking-widest uppercase">Our Network</span>
            </div>
            <h2 className="font-playfair text-3xl md:text-5xl text-[#1C1C1C] font-semibold leading-tight">
              Trusted Suppliers<br />
              <em className="italic font-normal text-[#2D5E3F]">Around the Globe</em>
            </h2>
          </div>
          <div className="lg:max-w-xs">
            <p className="text-stone-600 text-sm leading-relaxed">
              We partner with industry-leading producers across Chile, Brazil, and Argentina to deliver consistent quality and reliability to the U.S. market.
            </p>
          </div>
        </div>

        {/* Suppliers grid — 3 columns of 3 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {suppliers.map((supplier, i) => (
            <div
              key={supplier.name}
              className="bg-white rounded-2xl p-6 border border-stone-100 hover:border-[#2D5E3F]/30 transition-all duration-300 cursor-default"
              style={{ animationDelay: `${i * 80}ms` }}
            >
              {/* Top */}
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#2D5E3F]/10">
                  <i className="ri-building-line text-[#2D5E3F] text-lg"></i>
                </div>
                <span className="text-lg" title={supplier.country}>{supplier.flag}</span>
              </div>

              {/* Name */}
              <h4 className="font-semibold text-[#1C1C1C] text-sm mb-1 leading-tight">{supplier.name}</h4>
              <div className="text-[#2D5E3F] text-xs font-medium mb-3">{supplier.country}</div>

              {/* Specialty */}
              <div className="px-2.5 py-1.5 bg-[#F7F3EC] rounded-lg mb-3">
                <span className="text-stone-600 text-xs leading-tight block">{supplier.specialty}</span>
              </div>

              {/* Description */}
              <p className="text-stone-500 text-xs leading-relaxed">{supplier.description}</p>
            </div>
          ))}
        </div>

        {/* "And more" banner */}
        <div className="mt-8 p-6 rounded-2xl bg-[#2D5E3F] text-white flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className="ri-add-circle-line text-white text-2xl"></i>
            </div>
            <div>
              <div className="font-semibold text-sm">And many more partners</div>
              <div className="text-white/70 text-xs mt-0.5">Our supplier network continues to grow — reach out to explore specific sourcing needs.</div>
            </div>
          </div>
          <button
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#2D5E3F] text-sm font-medium rounded-full hover:bg-[#F7F3EC] transition-colors duration-200 cursor-pointer"
          >
            Contact Us
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
